import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import urls from '../util/urls';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';
import queryString from 'query-string';

type Params = {
  patientId?: string | null;
  status?: string | null;
  type?: string | null;
  pageSize?: number | null;
  projectId?: string | null;
  nextPageToken?: string | null;
  cohortId?: string | null;
};

type EventsParams = {
  projectId?: string | null;
  level?: string | null;
  pageSize?: number | null;
  nextPageToken?: string | null;
  cohortId?: string | null;
};

interface HydrateOmicsExplorerParams {
  projectId: string;
  subjectId: string;
}

interface GetVariants {
  setId: string;
  filters?: Record<string, string>;
  nextPageToken?: string;
}

type SummaryFilter = Record<string, any>;
interface GetSummary {
  projectId: string;
  testId: string;
  params?: {
    somatic: {
      expressionFilter: SummaryFilter;
      copyNumberFilter: SummaryFilter;
      structuralFilter: SummaryFilter;
      shortFilter: SummaryFilter;
    };
    germline: {
      expressionFilter: SummaryFilter;
      copyNumberFilter: SummaryFilter;
      structuralFilter: SummaryFilter;
      shortFilter: SummaryFilter;
    };
  };
}
interface RunManifestRequest {
  projectId: string;
  manifestFileId: string;
  patientInfo: {
    patientId?: string | null;
    mrn?: string;
    firstName?: string | null;
    lastName?: string | null;
    dob?: string | null;
    gender?: string | null;
  };
}
interface GetManifestPatientInfo {
  projectId: string;
  manifestFileId: string;
}
export default class GenomicsService {
  genomicsAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.genomicsAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getProjectTests = (auth: ServiceAuth) => async (params: Params) => {
    const { projectId } = params;
    // This is kind of goofy, but to ensure empty query params
    // don't get sent in the request, we will manually add
    // them if they are truthy instead of just sending them
    // all through.
    const requestParams: Params = {};
    if (params && params.patientId) {
      requestParams.patientId = params.patientId;
    }

    if (params && params.status) {
      requestParams.status = params.status;
    }

    if (params && params.type) {
      requestParams.type = params.type;
    }

    if (params && params.pageSize) {
      requestParams.pageSize = params.pageSize;
    }

    if (params && params.nextPageToken) {
      requestParams.nextPageToken = params.nextPageToken;
    }

    if (params && params.cohortId) {
      requestParams.cohortId = params.cohortId;
    }

    return this.genomicsAPI(auth).get(
      urls.api.genomics.projectTests({
        params: requestParams,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        projectId,
      }),
    );
  };

  getProjectEvents = (auth: ServiceAuth) => async (params: EventsParams) => {
    const { projectId } = params;
    // This is kind of goofy, but to ensure empty query params
    // don't get sent in the request, we will manually add
    // them if they are truthy instead of just sending them
    // all through.
    const requestParams: EventsParams = {};
    if (params && params.level) {
      requestParams.level = params.level;
    }

    if (params && params.pageSize) {
      requestParams.pageSize = params.pageSize;
    }

    if (params && params.nextPageToken) {
      requestParams.nextPageToken = params.nextPageToken;
    }

    if (params && params.cohortId) {
      requestParams.cohortId = params.cohortId;
    }

    return this.genomicsAPI(auth).get(
      urls.api.genomics.projectEvents({
        params: requestParams,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        projectId,
      }),
    );
  };

  deleteProjectTests =
    (auth: ServiceAuth) => async (projectId: string, testId: string) => {
      return this.genomicsAPI(auth).delete(
        urls.api.genomics.deleteTest({
          projectId,
          testId,
        }),
      );
    };

  getProjectResources =
    (auth: ServiceAuth) => async (projectId: string, cohortId?: string) => {
      return this.genomicsAPI(auth).get(
        urls.api.genomics.getProjectResources({
          projectId,
          cohortId,
        }),
      );
    };

  indexFmi = (auth: ServiceAuth) => async (params: object) => {
    return this.genomicsAPI(auth).post(urls.api.genomics.fmiImport(), params);
  };

  indexAshion = (auth: ServiceAuth) => async (params: object) => {
    return this.genomicsAPI(auth).post(
      urls.api.genomics.ashionImport(),
      params,
    );
  };

  getTestById =
    (auth: ServiceAuth) =>
    async (projectId: string, testId: string, params: object) => {
      return this.genomicsAPI(auth).get(
        urls.api.genomics.projectTest({
          projectId,
          testId,
          params,
        }),
      );
    };

  createGenomicSet =
    (auth: ServiceAuth) =>
    async (account: string, params: object, queryParams?: object) => {
      return this.genomicsAPI(auth).post(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.ga4gh.genomicSets(account, null, queryParams),
        params,
      );
    };

  createExpressionSet =
    (auth: ServiceAuth) => async (account: string, params: object) => {
      return this.genomicsAPI(auth).post(
        urls.api.ga4gh.rnaSets(account),
        params,
      );
    };

  createCopyNumberSet =
    (auth: ServiceAuth) => async (account: string, params: object) => {
      return this.genomicsAPI(auth).post(
        urls.api.ga4gh.copyNumberSets(account),
        params,
      );
    };

  createStructuralSet =
    (auth: ServiceAuth) => async (account: string, params: object) => {
      return this.genomicsAPI(auth).post(
        urls.api.ga4gh.structuralVariantSets(account),
        params,
      );
    };

  createReadgroupSet =
    (auth: ServiceAuth) => async (account: string, params: object) => {
      return this.genomicsAPI(auth).post(
        urls.api.ga4gh.readSets(account),
        params,
      );
    };

  hydrateOmicsExplorer =
    (auth: ServiceAuth) =>
    ({ projectId, subjectId }: HydrateOmicsExplorerParams) => {
      return this.genomicsAPI(auth).get(
        urls.api.genomics.hydrateOmicsExplorer({
          projectId,
          subjectId,
        }),
      );
    };

  getShortVariantsCounts =
    (auth: ServiceAuth) => (shortVariantSetId: string) => {
      return this.genomicsAPI(auth).post(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.genomics.variants(null, null, true),
        {
          variantSetIds: shortVariantSetId,
          include: 'count',
        },
      );
    };

  getCopyNumbersCounts = (auth: ServiceAuth) => (copyNumberSetId: string) => {
    return this.genomicsAPI(auth).post(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      urls.api.genomics.copyNumbers(null, null, true),
      {
        copyNumberSetIds: copyNumberSetId,
        include: 'count',
      },
    );
  };

  getStructuralVariantsCounts =
    (auth: ServiceAuth) => (structuralSetId: string) => {
      return this.genomicsAPI(auth).post(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.genomics.structuralVariants(null, null, true),
        {
          structuralVariantSetIds: structuralSetId,
          include: 'count',
        },
      );
    };

  getExpressionsCounts = (auth: ServiceAuth) => (expressionSetId: string) => {
    return this.genomicsAPI(auth).post(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      urls.api.genomics.expressions(null, null, true),
      {
        rnaQuantificationSetIds: expressionSetId,
        include: 'count',
      },
    );
  };

  getShortVariants =
    (auth: ServiceAuth) =>
    ({ setId, filters, nextPageToken }: GetVariants) => {
      const params: {
        filters?: Array<string>;
        include?: string;
        nextPageToken?: string | Array<string>;
        pageSize: number;
        variantSetIds: string;
      } = {
        variantSetIds: setId,
        pageSize: 30,
        include: 'drugAssociations,vcf,ensembl',
        ...filters,
      };

      if (nextPageToken) {
        params.nextPageToken = nextPageToken;
      }

      return this.genomicsAPI(auth).post(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.genomics.variants(null, null, true),
        queryString.stringify(params),
      );
    };

  getStructuralVariants =
    (auth: ServiceAuth) =>
    ({ setId, filters, nextPageToken }: GetVariants) => {
      const params: {
        filters?: Array<string>;
        include?: string;
        nextPageToken?: string | Array<string>;
        pageSize: number;
        structuralVariantSetIds: string;
      } = {
        structuralVariantSetIds: setId,
        pageSize: 30,
        include: 'drugAssociations',
        ...filters,
      };

      if (nextPageToken) {
        params.nextPageToken = nextPageToken;
      }

      return this.genomicsAPI(auth).post(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.genomics.structuralVariants(null, null, true),
        queryString.stringify(params),
      );
    };

  getCopyNumbers =
    (auth: ServiceAuth) =>
    ({ setId, filters, nextPageToken }: GetVariants) => {
      const params: {
        filters?: Array<string>;
        include?: string;
        nextPageToken?: string | Array<string>;
        pageSize: number;
        copyNumberSetIds: string;
      } = {
        copyNumberSetIds: setId,
        pageSize: 30,
        include: 'drugAssociations',
        ...filters,
      };

      if (nextPageToken) {
        params.nextPageToken = nextPageToken;
      }

      return this.genomicsAPI(auth).post(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.genomics.copyNumbers(null, null, true),
        queryString.stringify(params),
      );
    };

  getExpressions =
    (auth: ServiceAuth) =>
    ({ setId, filters, nextPageToken }: GetVariants) => {
      const params: {
        filters?: Array<string>;
        include?: string;
        nextPageToken?: string | Array<string>;
        pageSize: number;
        rnaQuantificationSetIds: string;
      } = {
        rnaQuantificationSetIds: setId,
        pageSize: 30,
        include: 'drugAssociations',
        ...filters,
      };

      if (nextPageToken) {
        params.nextPageToken = nextPageToken;
      }

      return this.genomicsAPI(auth).post(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.genomics.expressions(null, null, true),
        queryString.stringify(params),
      );
    };

  getSummary =
    (auth: ServiceAuth) =>
    async ({ projectId, testId, params }: GetSummary) => {
      return this.genomicsAPI(auth).post(
        urls.api.genomics.summary(projectId, testId),
        {
          ...params,
        },
      );
    };

  getManifestPatientInfo =
    (auth: ServiceAuth) =>
    async ({ projectId, manifestFileId }: GetManifestPatientInfo) => {
      return this.genomicsAPI(auth).get(
        urls.api.genomics.manifestPatientInfo(projectId, manifestFileId),
      );
    };

  runManifestRequest =
    (auth: ServiceAuth) => async (request: RunManifestRequest) => {
      return this.genomicsAPI(auth).post(
        urls.api.genomics.manifestRun(),
        request,
      );
    };
}
